import katex from 'katex';
import DOMPurify from 'dompurify';

// Allow KaTeX's output (MathML, SVG, custom attributes) through DOMPurify
DOMPurify.addHook('uponSanitizeElement', (_node, data) => {
  if (data.tagName && data.tagName.match(/^(math|semantics|m[a-z]+)$/i)) {
    data.allowedTags[data.tagName] = true;
  }
});

const PURIFY_CONFIG = {
  ADD_TAGS: [
    'math', 'semantics', 'mrow', 'mi', 'mn', 'mo', 'ms', 'mtext',
    'mfrac', 'msqrt', 'mroot', 'msup', 'msub', 'msubsup', 'munder',
    'mover', 'munderover', 'mtable', 'mtr', 'mtd', 'mspace',
    'annotation', 'annotation-xml',
    'svg', 'path', 'line', 'rect', 'circle', 'g', 'use', 'defs',
  ],
  ADD_ATTR: [
    'mathvariant', 'stretchy', 'fence', 'separator', 'lspace', 'rspace',
    'accent', 'accentunder', 'columnalign', 'rowalign', 'columnspacing',
    'rowspacing', 'columnlines', 'rowlines', 'frame', 'framespacing',
    'displaystyle', 'scriptlevel', 'minsize', 'maxsize', 'movablelimits',
    'encoding', 'linethickness', 'href', 'depth', 'width', 'height',
    'preserveAspectRatio', 'viewBox', 'fill', 'stroke', 'd',
    'xmlns', 'xlink:href', 'clip-path', 'fill-rule', 'stroke-width',
  ],
  ALLOW_DATA_ATTR: false,
};

function decodeHtmlEntities(text) {
  let prev = text;
  let decoded = new DOMParser().parseFromString(prev, 'text/html').body.innerHTML;
  let maxIterations = 5;
  while (decoded !== prev && maxIterations-- > 0) {
    prev = decoded;
    decoded = new DOMParser().parseFromString(prev, 'text/html').body.innerHTML;
  }
  return decoded;
}

// Whether KaTeX knows the control word (one that only lacks its argument, like \frac, counts).
const knownCommands = new Map();
function isKnownCommand(name) {
  if (!knownCommands.has(name)) {
    let known = true;
    try {
      katex.renderToString(`\\${name}`, { throwOnError: true });
    } catch (error) {
      known = !/Undefined control sequence/.test(error.message);
    }
    knownCommands.set(name, known);
  }
  return knownCommands.get(name);
}

function decodeMathEntities(math) {
  const el = document.createElement('textarea');
  el.innerHTML = math;
  // The docx import glues a symbol command to the letters after it (\geqx, \pmy, \angleABD):
  // a control word KaTeX does not know is split after the longest command it starts with.
  return el.value.replace(/\\([a-zA-Z]+)/g, (match, name) => {
    if (isKnownCommand(name)) return match;
    for (let end = name.length - 1; end > 0; end--) {
      const head = name.slice(0, end);
      if (isKnownCommand(head)) return `\\${head} ${name.slice(end)}`;
    }
    return match;
  });
}

function renderKatex(math, displayMode = false) {
  return katex.renderToString(decodeMathEntities(math), {
    throwOnError: false,
    output: 'htmlAndMathml',
    displayMode,
  });
}

function processMath(text, dollarMath = true) {
  const placeholders = [];

  // 1. Protect and render delimited math first, replacing with placeholders
  let result = text
    .replace(/\\\[([\s\S]*?)\\\]/g, (_, math) => {
      const rendered = renderKatex(math, true);
      placeholders.push(rendered);
      return `\x00MATH${placeholders.length - 1}\x00`;
    })
    .replace(/\\\(([\s\S]*?)\\\)/g, (_, math) => {
      const rendered = renderKatex(math);
      placeholders.push(rendered);
      return `\x00MATH${placeholders.length - 1}\x00`;
    });
  // Prose (passages) keeps single dollars as they are: "$5 and $10" are prices, not math.
  if (dollarMath) {
    result = result.replace(/\$(.*?)\$/g, (_, math) => {
      const rendered = renderKatex(math);
      placeholders.push(rendered);
      return `\x00MATH${placeholders.length - 1}\x00`;
    });
  }

  // 2. Replace stray LaTeX commands (outside delimiters) with Unicode
  //    (?![a-zA-Z]) ensures \in doesn't match inside \int, \infty, etc.
  result = result
    .replace(/\\rightarrow(?![a-zA-Z])/g, '→')
    .replace(/\\leftarrow(?![a-zA-Z])/g, '←')
    .replace(/\\Rightarrow(?![a-zA-Z])/g, '⇒')
    .replace(/\\Leftarrow(?![a-zA-Z])/g, '⇐')
    .replace(/\\leftrightarrow(?![a-zA-Z])/g, '↔')
    .replace(/\\Leftrightarrow(?![a-zA-Z])/g, '⇔')
    .replace(/\\implies(?![a-zA-Z])/g, '⟹')
    .replace(/\\iff(?![a-zA-Z])/g, '⟺')
    .replace(/\\infty(?![a-zA-Z])/g, '∞')
    .replace(/\\times(?![a-zA-Z])/g, '×')
    // Operators often concatenated with next token in backend output (\pmy, \mpsinx, \bulletcosy)
    .replace(/\\bullet/g, '⋅')
    .replace(/\\cdot/g, '⋅')
    .replace(/\\pm(?!atrix|od)/g, '±')
    .replace(/\\mp(?!atrix)/g, '∓')
    .replace(/\\neq(?![a-zA-Z])/g, '≠')
    .replace(/\\leq(?![a-zA-Z])/g, '≤')
    .replace(/\\geq(?![a-zA-Z])/g, '≥')
    .replace(/\\approx(?![a-zA-Z])/g, '≈')
    .replace(/\\angle(?![a-zA-Z])/g, '∠')
    .replace(/\\parallel(?![a-zA-Z])/g, '∥')
    .replace(/\\in(?![a-zA-Z])/g, '∈');

  // 3. Restore placeholders
  result = result.replace(/\x00MATH(\d+)\x00/g, (_, idx) => placeholders[parseInt(idx)]);

  return result;
}

const COMPONENT_STYLES = `
.katex-component table {
  border-collapse: collapse;
  margin: 8px 0;
  border: 1px solid #e2e8f0;
}
.katex-component table td,
.katex-component table th {
  border: 1px solid #e2e8f0;
  padding: 6px 12px;
  vertical-align: top;
}
.katex-component table th {
  background-color: #f1f5f9;
  font-weight: 600;
  text-align: left;
}
.katex-component table tr:nth-child(even) td {
  background-color: #f8fafc;
}
.katex-component img {
  display: inline-block;
  max-width: 100%;
  height: auto;
}
`;

export default function KaTeXComponent({ text, dollarMath = true }) {
  const decoded = decodeHtmlEntities(text);
  const processed = DOMPurify.sanitize(processMath(decoded, dollarMath), PURIFY_CONFIG);

  return (
    <div className="katex-component">
      <style>{COMPONENT_STYLES}</style>
      <div dangerouslySetInnerHTML={{ __html: processed }} />
    </div>
  );
}
